// Login-phase telemetry to Application Insights. One page load is one login
// journey (loginJourneyId); an MSAL redirect login spans two page loads and thus
// two journeys — correlate via session_Id in the portal. After the initial
// login completes, success events become no-ops so silent refresh cycles do
// not pollute the login funnel; failures are always sent with trigger
// 'refresh' instead of 'initial'.

import * as UUID from 'uuid'
import { appInsights } from './appInsights'
import { getConsoleBufferSnapshot } from './consoleBuffer'
import { isOfficeInitialized } from './outlookHelper'

export type LoginPhase = 'started' | 'redirect-callback' | 'bootstrap' | 'hosts' | 'tokens' | 'authenticated' | 'reactive-refresh'
export type LoginMode = 'office-sso' | 'msal-silent' | 'msal-redirect' | 'storage'

const EVENT_NAMES: Record<LoginPhase, string> = {
    started: 'Login.Started',
    'redirect-callback': 'Login.Failed',
    bootstrap: 'Login.BootstrapAcquired',
    hosts: 'Login.HostsAcquired',
    tokens: 'Login.TokensAcquired',
    authenticated: 'Login.Authenticated',
    'reactive-refresh': 'Login.Failed',
}

export const loginJourneyId = UUID.v4()
const startedAt = performance.now()
let initialLoginComplete = false
let tokensFetchedThisSession = false

function getRuntime(): 'standalone' | 'outlook-windows' | 'outlook-mac' | 'outlook-web' {
    if (!isOfficeInitialized()) {
        return 'standalone'
    }
    if (Office.PlatformType.PC === Office.context.platform) {
        return 'outlook-windows'
    }
    if (Office.PlatformType.Mac === Office.context.platform) {
        return 'outlook-mac'
    }
    return 'outlook-web'
}

function commonProperties(): Record<string, unknown> {
    return {
        journeyId: loginJourneyId,
        trigger: initialLoginComplete ? 'refresh' : 'initial',
        runtime: getRuntime(),
        elapsedMs: Math.round(performance.now() - startedAt),
    }
}

function extractErrorDetails(error: unknown): Record<string, unknown> {
    const e = error as {
        message?: string
        code?: number | string
        errorCode?: string
        response?: { status?: number }
    }
    return {
        errorMessage: String(e?.message ?? e ?? '').substring(0, 1000),
        errorCode: e?.errorCode ?? e?.code,
        httpStatus: e?.response?.status,
    }
}

export function trackLoginPhase(phase: LoginPhase, properties?: Record<string, unknown>): void {
    if (initialLoginComplete) {
        return
    }
    appInsights.trackEvent({
        name: EVENT_NAMES[phase],
        properties: {
            ...commonProperties(),
            ...(phase === 'authenticated' ? { viaStorage: !tokensFetchedThisSession } : {}),
            ...properties,
        },
    })
}

export function trackLoginFailure(phase: LoginPhase, error: unknown, properties?: Record<string, unknown>): void {
    appInsights.trackEvent({
        name: 'Login.Failed',
        properties: {
            ...commonProperties(),
            phase,
            ...extractErrorDetails(error),
            ...properties,
            consoleLog: getConsoleBufferSnapshot(),
        },
    })
}

export function markInitialLoginComplete(): void {
    initialLoginComplete = true
}

export function markTokensFetched(): void {
    tokensFetchedThisSession = true
}

/** Push pending telemetry out before a redirect navigates away. */
export function flushLoginTelemetry(): void {
    appInsights.flush()
}
